import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { Loader2, Bot } from 'lucide-react';
import { useAuth } from './hooks/useAuth';
import { GoogleSignInSection } from './GoogleSignInButton';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional()
});

type LoginForm = z.infer<typeof loginSchema>;

export const LoginPage = () => {
  const { login, googleSignIn, isLoading } = useAuth();
  
  const form = useForm<LoginForm>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false
    }
  });

  const onSubmit = (data: LoginForm) => {
    login({
      email: data.email,
      password: data.password
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50/60 dark:bg-slate-950 py-6 px-4 sm:px-6 transition-colors">
      <div className="max-w-[390px] w-full space-y-3.5">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1.5">
            <div className="p-1 rounded-lg bg-primary/10 text-primary">
              <Bot className="h-5 w-5" />
            </div>
            <h1 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">OmnibizChat</h1>
          </div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">
            Sign in to your account
          </h2>
          <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
            Or{' '}
            <Link to="/register" className="font-semibold text-primary hover:text-primary/80 transition-colors">
              create a new account
            </Link>
          </p>
        </div>
      
        <Card className="p-5 sm:p-6 shadow-sm border-slate-200/80 dark:border-slate-800/80 rounded-2xl bg-white dark:bg-slate-900">
          <GoogleSignInSection
            context="signin"
            onCredential={(credential) => googleSignIn({ credential })}
            disabled={isLoading}
          />

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="email" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Email address
              </Label>
              <Input
                {...form.register('email')}
                type="email"
                autoComplete="email"
                className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                placeholder="Enter your email"
              />
              {form.formState.errors.email && (
                <p className="text-error text-xs mt-0.5">
                  {form.formState.errors.email.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <Label htmlFor="password" className="text-xs font-medium text-slate-700 dark:text-slate-300">
                Password
              </Label>
              <Input
                {...form.register('password')}
                type="password"
                autoComplete="current-password"
                className="h-9 px-3 text-xs rounded-lg border-slate-200 dark:border-slate-800"
                placeholder="Enter your password"
              />
              {form.formState.errors.password && (
                <p className="text-error text-xs mt-0.5">
                  {form.formState.errors.password.message}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-0.5">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  {...form.register('rememberMe')}
                  id="remember-me"
                  className="rounded"
                />
                <Label htmlFor="remember-me" className="text-xs text-slate-600 dark:text-slate-400 cursor-pointer font-normal">
                  Remember me
                </Label>
              </div>
            </div>

            <div className="pt-1">
              <Button 
                type="submit" 
                className="w-full h-10 py-2.5 px-4 rounded-lg text-sm font-medium shadow-sm hover:shadow transition-all flex items-center justify-center gap-2" 
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </div>
          </form>

          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-slate-800/80 text-center">
            <p className="text-[11px] text-slate-400 dark:text-slate-500 leading-relaxed">
              Start automating your customer service across Facebook, Instagram, and WhatsApp.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};